import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Card, CardText, CardImage } from '@blueprint/card';
import { Image } from '@blueprint/image';
import { Text } from '@blueprint/text';
import { FeatureList } from 'shared/components/listing-data/listing-features';
import {
    CardImagePlaceholder,
    PropertyImage
} from 'shared/components/listing-data/listing-image';
import { Button } from '@blueprint/button';
import {
    BinOutlineSmall,
    EmailOutlineSmall,
    ShareiosSmall,
    ShareSmall
} from '@blueprint/icons';
import { ButtonWithIcon } from '@blueprint/ui';
import { TagWrapper } from 'shared/components/listing-data/lifecycle-tag';

export const SavedListingWrapper = styled(Card)(
    ({ theme: { spacing } }) => css`
        display: block;
        margin: ${spacing['16px']} 0 0;
        padding: 0;
        position: relative;

        &:last-child {
            margin-bottom: ${spacing['16px']};
        }
    `
);

type SavedListingDetailsProps = {
    isCompact: boolean;
};

export const SavedListingDetails = styled('div')<SavedListingDetailsProps>(
    ({ theme: { colors, fontSizes, fontWeight, spacing }, isCompact }) => {
        let mobileStyles;
        let detailsBorder = `border-bottom: 1px solid ${colors['graphic-divider']};`;
        let imgDimensions = {
            height: '13.75rem; /* 220px */',
            width: '20rem'
        };

        if (isCompact) {
            detailsBorder = '';
            imgDimensions = { height: '6.25rem /* 100px */', width: '7.5rem' };
            mobileStyles = css`
                ${SavedListingInfo} {
                    align-items: flex-start;
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    margin-right: 2.5rem;
                    padding: ${spacing['8px']};
                }

                ${TagWrapper} {
                    bottom: ${spacing['8px']};
                    left: ${spacing['8px']};
                    margin: 0;
                    max-width: 7rem;
                    overflow: hidden;
                    position: absolute;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                ${SavedListingAddress} {
                    font-size: ${fontSizes[0]};
                    font-weight: ${fontWeight.bold};
                }

                ${SavedListingPrice} {
                    margin: 0;
                }

                ${FeatureList} {
                    margin-top: 0;
                }
            `;
        }

        return css`
            align-items: stretch;
            display: flex;
            flex-direction: row;
            position: relative;
            ${detailsBorder}

            ${PropertyImage} {
                align-items: center;
                display: flex;
                flex-grow: 0;
                flex-shrink: 0;
                flex-basis: ${imgDimensions.width};
                height: auto;
                justify-content: center;
                position: relative;
                width: 100%;
                text-align: center;
                max-width: ${imgDimensions.width};
                min-height: ${imgDimensions.height};

                ${CardImage} {
                    height: 100%;
                    left: 0;
                    margin: 0;
                    min-width: 0;
                    object-fit: cover;
                    object-position: center center;
                    position: absolute;
                    right: 0;
                    top: 0;
                    width: 100%;
                }

                ${CardImagePlaceholder} {
                    position: absolute;
                }
            }

            ${mobileStyles}
        `;
    }
);

export const SavedListingInfo = styled('div')(
    ({ theme: { spacing } }) => css`
        align-items: flex-start;
        display: flex;
        flex: 1 1 100%;
        flex-direction: column;
        padding: ${spacing['16px']};

        ${FeatureList} {
            display: flex;
            flex-direction: row;
            margin-top: ${spacing['16px']};
        }
    `
);

export const SavedListingTitle = styled(CardText)(
    ({ theme: { colors, fontSizes, fontWeight } }) =>
        css`
            color: ${colors['neutral-6']};
            font-size: ${fontSizes[0]};
            font-weight: ${fontWeight.bold};
            text-transform: uppercase;
        `
);

export const SavedListingPrice = styled(CardText)(
    ({ theme: { fontWeight, spacing } }) => css`
        font-size: 1.25rem; /* 20px */
        font-weight: ${fontWeight.bold};
        line-height: 1.4; /* 28px */
        margin: ${spacing['4px']} 0;
    `
);

export const SavedListingAddress = styled(CardText)(
    ({ theme: { colors } }) => css`
        color: ${colors['neutral-6']};
    `
);

export const SavedListingTransportList = styled('ul')(css`
    display: block;
    list-style: none;
    margin: 0;
    padding: 0;
`);

export const SavedListingTransports = styled('li')(
    ({ theme: { spacing } }) => css`
        :first-of-type {
            margin-top: ${spacing['16px']};
        }
    `
);

export const SavedListingCardFooter = styled('div')(
    ({ theme: { spacing } }) => css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        padding: 0.75rem ${spacing['16px']}; /* 12px x 16px */
    `
);

export const SavedListingFooterInfoBlock = styled('div')(css`
    align-items: center;
    display: flex;
    flex-direction: row;
`);

export const SavedListingBranchImage = styled(Image)(
    ({ theme: { spacing } }) => css`
        height: 2.5rem; /* 40px */
        margin-right: ${spacing['16px']};
        width: auto;
    `
);

export const SavedListingPublishedInfo = styled(Text)(
    ({ theme: { fontSizes } }) => css`
        font-size: ${fontSizes[1]};
    `
);

export const SavedListingActions = styled('div')`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

export const StyledButton = styled(Button)(
    ({ theme: { spacing } }) => css`
        svg {
            margin-right: ${spacing['8px']};
        }
    `
);

export const StyledLink = StyledButton.withComponent('a');

export const SavedListingLink = styled('a')(
    ({ theme: { focusRing } }) => css`
        display: block;
        text-decoration: none;

        &:focus {
            box-shadow: ${focusRing};
            outline: none;
        }

        ::after {
            bottom: 0;
            content: '';
            left: 0;
            opacity: 0;
            position: absolute;
            right: 0;
            top: 0;
        }
    `
);

export const CompactListingCard = styled('div')(
    ({ theme: { spacing } }) => css`
        display: block;
        margin-top: ${spacing['16px']};
        position: relative;
        text-decoration: none;

        > div {
            box-shadow: none;
            margin-bottom: 0;
        }

        &:last-child {
            margin-bottom: ${spacing['16px']};
        }
    `
);

export const CompactListingLink = styled(CompactListingCard)(
    ({ theme: { focusRing } }) => css`
        &:focus {
            box-shadow: ${focusRing};
            outline: none;
        }
    `
).withComponent('a');

export const MobileOptionsButton = styled(ButtonWithIcon)(
    ({ theme: { focusRing, spacing } }) => css`
        display: block;
        position: absolute;
        right: 0;
        top: 0;

        &:focus {
            box-shadow: inset ${focusRing};
            outline: 0;
        }

        svg {
            height: ${spacing['16px']};
            width: ${spacing['16px']};
        }
    `
);

export const BinOutlineColored = styled(BinOutlineSmall)(
    ({ theme: { colors } }) => css`
        color: ${colors['crimson']};
    `
);

export const ShareSmallColored = styled(ShareSmall)(
    ({ theme: { colors } }) => css`
        color: ${colors['purple-bright']};
    `
);

export const ShareiosSmallColored = styled(ShareiosSmall)(
    ({ theme: { colors } }) => css`
        color: ${colors['purple-bright']};
    `
);

export const EmailOutlineSmallColored = styled(EmailOutlineSmall)(
    ({ theme: { colors } }) => css`
        color: ${colors['purple-bright']};
    `
);
